import { Label } from "layout/typography/Label";
import { Surface } from "pages/HospitalsPage/components/BenefitSelection/Surface";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputTextarea } from "primereact/inputtextarea";
import { Message } from "primereact/message";
import { useState } from "react";
import { useLazyClassifyProcedureCodesQuery } from "store/queries/procedureModalityRange";
import { IProcedureModalityClassifyResult } from "types/ProcedureModalityRange/ProcedureModalityRange";

import { classificationInWords } from "./cptGroupWording";

/** The most codes the API classifies in one call (`PROCEDURE_MODALITY_CLASSIFY_CODES_MAX`). */
export const CLASSIFY_CODES_MAX = 50;

/** Pasted text → codes: split on commas, spaces and new lines; blanks and repeats dropped. */
export const codesFromText = (text: string): string[] => [
  ...new Set(
    text
      .split(/[\s,;]+/)
      .map((code) => code.trim())
      .filter(Boolean)
  ),
];

interface ICptGroupTesterProps {
  visible: boolean;
  onHide: () => void;
}

/**
 * CLE-3000 — "Classify codes", a dialog opened from the tab's button row: paste CPT / HCPCS codes
 * and see each one's group, through the classify endpoint — the same loader and classifier as a
 * real eligibility check, so it reads the ranges as saved. Nothing is stored.
 */
export function CptGroupTester({ visible, onHide }: ICptGroupTesterProps) {
  const [text, setText] = useState("");
  const [result, setResult] = useState<IProcedureModalityClassifyResult | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);
  const [classify, { isFetching }] = useLazyClassifyProcedureCodesQuery();

  const codes = codesFromText(text);
  const tooMany = codes.length > CLASSIFY_CODES_MAX;

  const run = async () => {
    if (codes.length === 0 || tooMany) return;
    setError(null);
    try {
      setResult(await classify(codes).unwrap());
    } catch (caught: any) {
      setResult(null);
      setError(
        caught?.data?.message ||
          caught?.message ||
          "The codes could not be classified."
      );
    }
  };

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      header="Classify codes"
      style={{ width: "min(560px, 95vw)" }}
      data-testid="cptGroups.testerDialog"
    >
      <Surface data-testid="cptGroups.tester">
        <div className="flex flex-column gap-1 mb-3">
          <Label htmlFor="cptGroups.tester.codes">CPT / HCPCS codes</Label>
          <InputTextarea
            id="cptGroups.tester.codes"
            data-testid="cptGroups.tester.codes"
            rows={3}
            value={text}
            onChange={(event) => setText(event.target.value)}
            placeholder="70553, 78452, 95810"
          />
          <small className="text-color-secondary">
            Separate with commas, spaces or new lines. Modifiers are ignored
            (76700-26 is 76700). At most {CLASSIFY_CODES_MAX}.
          </small>
        </div>

        <Button
          type="button"
          label="Classify"
          icon="pi pi-play"
          className="p-button-sm mb-3"
          loading={isFetching}
          disabled={codes.length === 0 || tooMany}
          onClick={run}
          data-testid="cptGroups.tester.run"
        />

        {tooMany && (
          <Message
            severity="warn"
            text={`At most ${CLASSIFY_CODES_MAX} codes at a time.`}
          />
        )}
        {error && <Message severity="error" text={error} />}

        {result && (
          <div
            className="flex flex-column gap-1"
            data-testid="cptGroups.tester.result"
          >
            {result.usingSeedFallback && (
              <small
                className="text-color-secondary"
                data-testid="cptGroups.tester.fallback"
              >
                No ranges are saved yet, so these came from the built-in ranges.
              </small>
            )}
            {result.results.map((answer) => (
              <div
                key={answer.code}
                className="flex gap-2"
                data-testid={`cptGroups.tester.result.${answer.code}`}
              >
                <span className="font-mono" style={{ minWidth: "6rem" }}>
                  {answer.code}
                </span>
                <span
                  className={
                    answer.modality === "OTHER"
                      ? "text-color-secondary"
                      : "font-semibold"
                  }
                >
                  {classificationInWords(answer)}
                </span>
              </div>
            ))}
          </div>
        )}
      </Surface>
    </Dialog>
  );
}
